import { range, sum } from 'lodash';
import { nanoid } from 'nanoid';
import { jsx } from 'slate-hyperscript';

import { SlatePlugin } from '../../../../slatePlugins/slatePlugins.types';
import { TableElement, TablePluginOptions } from '../table/types';
import { renderAdminTable } from './components/AdminView/renderAdminTable';
import { BlogTableReadOnlyPlugin } from './BlogTableReadOnlyPlugin';

const defaultOptions: TablePluginOptions = {
  stickyHeaderOffset: 0,
};

const findMaxColumnInTable = (el) => {
  let result = 1;
  if (el?.rows?.length > 0) {
    for (let i = 0; i < el.rows.length; i++) {
      const row = el.rows[i];
      if (row?.cells?.length > result) {
        result = row.cells.length;
      }
    }
  }

  return result;
};

const deserializeTable = (el) => {
  let children = Array.from(el.childNodes).map(deserializeTable);

  if (children.length === 0) {
    children = [{ text: '' }] as any;
  }

  switch (el.nodeName) {
    case 'TABLE': {
      const defaultColumnWidth = 200;
      const columnCount = findMaxColumnInTable(el);
      const defaultCellsWidth = range(columnCount).map((_) => defaultColumnWidth);
      let cellsWidth = defaultCellsWidth;
      const colGroup = Array.from(el.childNodes || []).find(
        (item: any) => item.nodeName === 'COLGROUP'
      ) as any;
      if (colGroup && colGroup.childNodes) {
        cellsWidth = Array.from(colGroup.childNodes).map((col: any) => {
          return Math.max(col?.width, 80) || defaultColumnWidth;
        }) as any;
        cellsWidth.length = columnCount;
      }
      const width = sum(cellsWidth);
      if (columnCount > 0) {
        return jsx(
          'element',
          {
            type: TableElement.TABLE,
            id: nanoid(),
            cellsWidth,
            width,
            border: true,
            hasRowHeading: true,
          },
          children
        );
      }
      return undefined;
    }
    case 'TR': {
      return jsx('element', { type: TableElement.TR, id: nanoid() }, children);
    }
    case 'TH':
    case 'TD': {
      const cellObj: any = { type: TableElement.TD, id: nanoid() };
      if (el.colSpan > 1) {
        cellObj.colSpan = el.colSpan;
        cellObj.rowSpan = el?.rowSpan || 1;
      }
      if (el.rowSpan > 1) {
        cellObj.rowSpan = el.rowSpan;
        cellObj.colSpan = el?.colSpan || 1;
      }
      return jsx('element', cellObj, children);
    }
    case 'COLGROUP':
    default:
  }

  return undefined;
};

export const BlogTablePlugin = (opts: TablePluginOptions = {}): SlatePlugin => {
  const options = { ...defaultOptions, ...opts };
  if (opts.isReadOnly) {
    return BlogTableReadOnlyPlugin(opts);
  }

  return {
    renderElement: renderAdminTable(options),

    deserialize: {
      element: [
        {
          type: TableElement.TABLE,
          deserialize: deserializeTable,
        },
      ],
    },
  };
};
